import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo } from 'react'

import { MobXProviderContext, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { LocationType } from '~/client/graph'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import type { ListSelectorTitleVariant } from '~/client/src/shared/components/ListSelector/ListSelector'
import type {
  IListSelectorGroupConfig,
  IListSelectorSearchConfig,
  IListSelectorSortConfig,
} from '~/client/src/shared/components/ListSelector/types'
import WorkflowTypeIcon from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowTypeIcon'
import InactiveWorkflowTypeInfo from '~/client/src/shared/components/WorkflowTypeListSelector/InactiveWorkflowTypeInfo'
import { ManageWorkflowTypesContext } from '~/client/src/shared/components/WorkflowTypeListSelector/ManageWorkflowTypesContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { compareGroupLabelsUnassignedLast } from '~/client/src/shared/utils/compareGroupsByLabel'
import { evaluateFilters } from '~/client/src/shared/utils/filterEvaluationHelpers'
import { areArraysEqualUnordered } from '~/client/src/shared/utils/util'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

interface IWorkflowTypeItem {
  id: string
  name: string
  iconName: string
  categoryName: string
  // In the list but toggled off in the Workflow Configurator (isEnabled false).
  isInactive: boolean
}

interface IBaseProps {
  // Source list: actual types (current versions, including inactive ones) vs
  // enabled-only types. Matches the legacy engine's useActualTypes flag.
  useActualTypes?: boolean
  // Restrict the list to this allow-list of workflow-type ids.
  allowedTypeIds?: string[]
  // Restrict the list to types valid for this location (see selectSourceTypes).
  filterByLocation?: LocationBase
  // With filterByLocation: keep any type that has location/equipment family
  // fields, instead of requiring a locationField that passes its filters.
  includeInformationalLocationFields?: boolean
  className?: string
  // Initial grouping; null renders flat. Omit to keep the default.
  defaultGroupKey?: string | null
  // Action behind the inactive-type tooltip's "Workflow Configurator" link.
  // Takes precedence over ManageWorkflowTypesContext; when neither is present the
  // link renders as plain text. Keeping navigation in the caller keeps this
  // shared selector app-agnostic (no desktop store / page knowledge).
  onManageInConfigurator?: () => void
  // Host caps the selector with `max-h-*` instead of pinning a height.
  parentSizesToContent?: boolean
  // In-panel Title band (mirrors CompanyListSelector), so the header lives in the
  // same ListSelector.Root as the list (no white header/gutters).
  title?: string
  titleVariant?: ListSelectorTitleVariant
  onClose?: () => void
}

interface ISingleProps extends IBaseProps {
  mode: 'single'
  selectedId?: string
  onSelect: (workflowTypeId: string) => void
}

interface IMultiProps extends IBaseProps {
  mode?: 'multi'
  selectedIds: string[]
  // Live selection callback (fires on every toggle). Use this OR the
  // showActions/onApply draft flow below.
  onSelectionChange?: (workflowTypeIds: string[]) => void
  // With showActions the selector renders its own Cancel/Apply footer and
  // manages a draft.
  showActions?: boolean
  onApply?: (workflowTypeIds: string[]) => void
  disableApplyWhenEmpty?: boolean
  applyLabel?: string
}

type IProps = ISingleProps | IMultiProps

const CATEGORY_GROUP_KEY = 'category'
const ACTIVE_STATE_GROUP_KEY = 'active-state'

// Band ids for the Active/Not Active grouping. Stable and unlocalized: they key
// the collapse state and the tree's ancestor lookups, while the header text
// comes from getGroupLabel.
const ACTIVE_BAND = 'active'
const INACTIVE_BAND = 'inactive'
// Leaf keys are `<band><BAND_SEPARATOR><category>`, making the grouping
// two-band: Active / Not Active, then category within each. A category name is
// user-entered free text, so the separator has to be a character it can never
// contain — NUL, not a printable one.
const BAND_SEPARATOR = '\u0000'

// The key format has exactly one writer and one reader — keep them adjacent.
function makeBandKey(isInactive: boolean, categoryName: string): string {
  return (
    (isInactive ? INACTIVE_BAND : ACTIVE_BAND) + BAND_SEPARATOR + categoryName
  )
}

// [band, category] — category is null for a band's own (ancestor) key.
function splitBandKey(key: string): [string, string | null] {
  const index = key.indexOf(BAND_SEPARATOR)
  return index < 0 ? [key, null] : [key.slice(0, index), key.slice(index + 1)]
}

// A leaf's ancestor list is one of three fixed values. Hoisted so the lookup —
// which CardList runs per item and per group on every render — allocates nothing.
const NO_ANCESTORS: readonly string[] = []
const ACTIVE_ANCESTORS: readonly string[] = [ACTIVE_BAND]
const INACTIVE_ANCESTORS: readonly string[] = [INACTIVE_BAND]

function bandAncestors(key: string): readonly string[] {
  const index = key.indexOf(BAND_SEPARATOR)
  if (index < 0) return NO_ANCESTORS
  return key.startsWith(ACTIVE_BAND) ? ACTIVE_ANCESTORS : INACTIVE_ANCESTORS
}

// Map saved ids to their current-version ids (de-duped). A superseded version
// id resolves to its current version; an id that doesn't resolve (deleted, or
// not loaded yet) is KEPT as-is so the saved selection is never silently pruned.
// Mirrors the legacy engine's `getActualType(id)?.id ?? id`.
function resolveToCurrentIds(
  ids: string[],
  workflowTypesStore: WorkflowTypesStore,
): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  ids.forEach(id => {
    const resolvedId =
      workflowTypesStore.getActualTypeByAnyVersionId(id)?.id ?? id
    if (!seen.has(resolvedId)) {
      seen.add(resolvedId)
      out.push(resolvedId)
    }
  })
  return out
}

// Port of WorkflowTypesSelectionList's `sourceCollection`: choose the base list,
// apply the allow-list, then narrow by location. (Deleted-selected append and
// closure gating are omitted — no caller exercises them: the sole prior
// includeDeletedSelected consumer pre-filtered deleted ids via getActualTypeByType,
// so deleted types were never shown here even on the legacy engine.)
function selectSourceTypes(opts: {
  workflowTypesStore: WorkflowTypesStore
  state: InitialState
  useActualTypes?: boolean
  allowedTypeIds?: string[]
  filterByLocation?: LocationBase
  includeInformationalLocationFields?: boolean
}): WorkflowType[] {
  const {
    workflowTypesStore,
    state,
    useActualTypes,
    allowedTypeIds,
    filterByLocation,
    includeInformationalLocationFields,
  } = opts

  let types = useActualTypes
    ? workflowTypesStore.actualTypes
    : workflowTypesStore.enabledTypes

  if (allowedTypeIds?.length) {
    types = types.filter(t => allowedTypeIds.includes(t.id))
  }

  if (!filterByLocation) return types

  const { isAdminOrFormsMaster, companyId } = state.userActiveProjectSettings
  return types.filter(t => {
    if (includeInformationalLocationFields) {
      const familyFields = filterByLocation.is(LocationType.OffloadingEquipment)
        ? t.allEquipmentFields
        : t.allLocationFields
      return familyFields.length > 0
    }
    if (!t.locationField) return false
    if (!t.locationField.filterOptions?.length) return true
    return (
      evaluateFilters(t.locationField.filterOptions, filterByLocation) &&
      (isAdminOrFormsMaster ||
        filterByLocation.hasRestrictionForCompany(companyId))
    )
  })
}

function WorkflowTypeListSelector(props: IProps) {
  const {
    useActualTypes,
    allowedTypeIds,
    filterByLocation,
    includeInformationalLocationFields,
    className,
    defaultGroupKey,
    onManageInConfigurator,
    parentSizesToContent,
    title,
    titleVariant,
    onClose,
  } = props
  const isSingle = props.mode === 'single'

  const { workflowTypesStore, workflowCategoriesStore, state, isMobileEnv } =
    useContext(MobXProviderContext) as {
      workflowTypesStore: WorkflowTypesStore
      workflowCategoriesStore: WorkflowCategoriesStore
      state: InitialState
      // Spread from the root store; true on the mobile build.
      isMobileEnv?: boolean
    }

  // Prop wins; otherwise fall back to the app-provided handler (desktop wires
  // navigation to the Workflow Configurator). Null on both → plain-text link.
  const contextManage = useContext(ManageWorkflowTypesContext)
  const manageInConfigurator = onManageInConfigurator ?? contextManage

  const multiSelectedIds = isSingle ? null : props.selectedIds
  const singleSelectedId = isSingle ? props.selectedId : undefined
  const onSelectionChange = isSingle ? undefined : props.onSelectionChange
  const showActions = isSingle ? false : props.showActions
  const onApply = isSingle ? undefined : props.onApply

  // Source + item projection are derived every render (not memoized): they read
  // observable store/model data (types, category names, enabled/deleted state,
  // location filters) that can load or mutate in place while the selector is
  // mounted, and the observer re-renders on those reads. Small list, cold path.
  const source = selectSourceTypes({
    workflowTypesStore,
    state,
    useActualTypes,
    allowedTypeIds,
    filterByLocation,
    includeInformationalLocationFields,
  })

  const items: IWorkflowTypeItem[] = source.map(type => ({
    id: type.id,
    name: type.name,
    iconName: type.iconName ?? '',
    categoryName: type.getCategoryName(workflowCategoriesStore) || UNASSIGNED,
    isInactive: type.isInactive,
  }))

  // Resolve the saved ids to current-version ids (unresolved ids kept). See
  // resolveToCurrentIds; used for display, the toggles, and the multi push-back.
  const rawSelectedIds =
    multiSelectedIds ?? (singleSelectedId ? [singleSelectedId] : [])
  const resolvedIds = resolveToCurrentIds(rawSelectedIds, workflowTypesStore)

  // The FULL resolved selection (including ids whose type isn't currently
  // displayed — deleted/hidden/pre-load). Hidden ids have no row so they don't
  // affect the rendered checkboxes, but they MUST stay in this set: draft mode
  // (showActions) seeds its internal draft from it and returns it on Apply, so
  // filtering to displayed ids here would silently drop a saved-but-hidden type.
  // Keyed on membership so the reference is stable across renders (draft mode
  // re-seeds on selectedIds identity); the key still changes when the resolved
  // set does (incl. once types finish loading), keeping load-race correctness.
  const resolvedKey = resolvedIds.join('|')
  const selectedIdsSet = useMemo(
    () => new Set(resolvedIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable by membership
    [resolvedKey],
  )

  // Echo the resolved set back so a save re-persists current ids rather than the
  // stale ones it was seeded with. Runs each render (resolvedIds is recomputed),
  // so it also corrects once types finish loading; the guard makes it a no-op
  // after convergence, and kept unresolved ids mean an unloaded store never
  // wipes the selection. Multi only.
  useEffect(() => {
    if (!onSelectionChange || !multiSelectedIds) return
    if (!areArraysEqualUnordered(resolvedIds, multiSelectedIds)) {
      onSelectionChange(resolvedIds)
    }
  }, [resolvedIds, multiSelectedIds, onSelectionChange])

  // Toggle against the FULL resolved selection, not just the displayed subset,
  // so hidden/unresolved saved ids survive an unrelated selection change.
  const handleItemToggle = (id: string) => {
    const next = new Set(resolvedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onSelectionChange?.(Array.from(next))
  }

  const handleToggleAll = (ids: string[], selectAll: boolean) => {
    const next = new Set(resolvedIds)
    ids.forEach(id => (selectAll ? next.add(id) : next.delete(id)))
    onSelectionChange?.(Array.from(next))
  }

  const searchConfig = useMemo<IListSelectorSearchConfig<IWorkflowTypeItem>>(
    () => ({
      searchFn: (item, query) =>
        item.name.toLowerCase().includes(query) ||
        item.categoryName.toLowerCase().includes(query),
      inputTestId: 'workflow-types-search-input',
    }),
    [],
  )

  const sortConfig = useMemo<IListSelectorSortConfig<IWorkflowTypeItem>>(
    () => ({
      options: [
        { key: 'name', label: Localization.translator.name, glyph: 'name' },
      ],
      defaultKey: 'name',
      sortFn: (a, b, sortOrder) => a.name.localeCompare(b.name) * sortOrder,
    }),
    [],
  )

  const groupingConfig = useMemo<IListSelectorGroupConfig<IWorkflowTypeItem>>(
    () => ({
      options: [
        {
          key: CATEGORY_GROUP_KEY,
          label: Localization.translator.category,
          glyph: 'workflowCategory',
        },
        {
          key: ACTIVE_STATE_GROUP_KEY,
          label: Localization.translator.activeNotActive,
          glyph: 'activeState',
        },
      ],
      // Active/Not Active leads wherever inactive types can appear, so the
      // ones that can't be created are separated out before the user scans.
      // Without useActualTypes the list is enabled-only, and an "Active" band
      // wrapping every category would be pure noise — stay flat there.
      defaultKey:
        defaultGroupKey !== undefined
          ? defaultGroupKey
          : useActualTypes
            ? ACTIVE_STATE_GROUP_KEY
            : CATEGORY_GROUP_KEY,
      buildGroupConfig: groupKey => {
        if (groupKey === ACTIVE_STATE_GROUP_KEY) {
          return {
            groupBy: item => [makeBandKey(item.isInactive, item.categoryName)],
            // Presence of this turns on CardList tree mode: the two band keys
            // become synthesized parent rows over their category leaves.
            getGroupAncestors: bandAncestors,
            getGroupLabel: key => {
              const [band, category] = splitBandKey(key)
              if (category !== null) return category
              return band === ACTIVE_BAND
                ? Localization.translator.active
                : Localization.translator.notActive
            },
            // Groups render in this order, so each band header has to land
            // immediately before its own categories.
            sortGroups: (a, b) => {
              const [bandA, categoryA] = splitBandKey(a)
              const [bandB, categoryB] = splitBandKey(b)
              if (bandA !== bandB) return bandA === ACTIVE_BAND ? -1 : 1
              if (categoryA === null) return -1
              if (categoryB === null) return 1
              return compareGroupLabelsUnassignedLast(categoryA, categoryB)
            },
          }
        }
        return {
          groupBy: item => [item.categoryName],
          getGroupLabel: key => key,
          sortGroups: compareGroupLabelsUnassignedLast,
        }
      },
    }),
    [useActualTypes, defaultGroupKey],
  )

  // Full-row renderer (rather than renderItemContent) so the row can carry a
  // per-type test id.
  const renderItem = useCallback(
    (item: IWorkflowTypeItem, isSelected: boolean, onToggle: () => void) => {
      // Muting tracks the type being off (STX1-763), NOT whether the row can be
      // picked — inactive types are selectable, they just read as inactive.
      const isMuted = item.isInactive
      return (
        <ListSelector.Row
          isSelected={isSelected}
          onToggle={onToggle}
          testId={`workflow-types-row-${item.id}`}
        >
          <WorkflowTypeIcon
            iconName={item.iconName}
            size="small"
            className={isMuted ? 'tw:opacity-40' : ''}
          />
          <span
            className={classList({
              'tw:truncate': true,
              'tw:text-muted-foreground': isMuted,
            })}
          >
            {item.name}
          </span>
          {isMuted && (
            <InactiveWorkflowTypeInfo
              isMobile={!!isMobileEnv}
              onManage={manageInConfigurator}
            />
          )}
        </ListSelector.Row>
      )
    },
    [isMobileEnv, manageInConfigurator],
  )

  return (
    <ListSelector.Root<IWorkflowTypeItem>
      items={items}
      selectedIds={selectedIdsSet}
      selectionMode={isSingle ? 'single' : 'multi'}
      onItemToggle={isSingle ? props.onSelect : handleItemToggle}
      onToggleAll={isSingle ? undefined : handleToggleAll}
      draft={showActions}
      // Root closes after a single pick; single-mode callers own that step.
      onClose={isSingle ? undefined : onClose}
      search={searchConfig}
      sort={sortConfig}
      grouping={groupingConfig}
      className={className ?? 'tw:w-full tw:h-full tw:bg-background'}
    >
      {title && (
        <ListSelector.Title
          title={title}
          onClose={onClose}
          variant={titleVariant}
        />
      )}
      <ListSelector.Toolbar />
      <ListSelector.Counter formatTotal={Localization.translator.xTypesCount} />
      <ListSelector.List<IWorkflowTypeItem>
        renderItem={renderItem}
        parentSizesToContent={parentSizesToContent}
      />
      {showActions && (
        <ListSelector.Actions
          onApply={ids => onApply?.(Array.from(ids))}
          onClose={onClose}
          disableApplyWhenEmpty={
            isSingle ? undefined : props.disableApplyWhenEmpty
          }
          applyLabel={isSingle ? undefined : props.applyLabel}
        />
      )}
    </ListSelector.Root>
  )
}

export default observer(WorkflowTypeListSelector)
